/**
 * FootballScene3D — Three.js scene orchestrator for football training.
 *
 * Mirrors the API of the Phaser BaseGameScene / FootballScene:
 *   - applyManifest(manifest) — build field, configure cameras
 *   - setActors(actors)       — create 3D player models
 *   - updateFrame(frame)      — animate player positions
 *   - switchCamera(cameraId)  — swap between top-down / QB perspective
 *   - applyAnnotations(anns)  — draw 3D route tubes, brackets, etc.
 *
 * Communicates with React via the shared EventBus (same events as Phaser).
 */

import * as THREE from 'three';
import EventBus from '../EventBus';
import RendererRegistry from '../RendererRegistry';
import FieldBuilder from './FieldBuilder';
import CameraController from './CameraController';
import AnnotationRenderer3D from './AnnotationRenderer3D';
import {
  createPlayer,
  disposePlayer,
  setPlayerLocomotion,
  updatePlayerMixers,
  setLabelsVisibleForCamera,
  applyReadyStance,
  makeTopDownPlayerMarker,
  resolvePlayerMarkerColor,
} from './PlayerModel';
import { preloadHumanoid } from './HumanoidLoader';
import { applyAssignment } from './PlayerModel';
import { preloadClipsForVerbs } from './ClipLoader';
import { resolveRole, meshFacingOffset } from './AssetManifest';
import { applyLOD } from './PlayerLOD';

/**
 * Resolve an actor's initial facing yaw (radians) in the renderer's
 * "+X = downfield" convention. NOT including the mesh forward-axis
 * offset — caller adds that on top.
 *
 * Resolution order:
 *   1. actor.initial_state.facing_yaw — authored per-actor in the
 *      scene DSL. Coach-authored value, taken verbatim.
 *   2. actor.initial_state.facing — legacy NEGATED-radian field;
 *      kept for backward compatibility with pre-Sprint-6 scenes.
 *   3. archetype_hint on the role: defense / lineman defenders face
 *      −X (back upfield); everyone else faces +X (downfield). The
 *      archetype hint covers scenes where the team field wasn't set
 *      (e.g. neutral-coverage drills authored in early sprints).
 *   4. Team-based fallback: 'defense' → −X, anything else → +X.
 *
 * Returns yaw in radians.
 */
function _resolveBaseFacing(actor) {
  const initial = actor && actor.initial_state;
  if (initial) {
    if (typeof initial.facing_yaw === 'number') return initial.facing_yaw;
    const legacy = initial.facing;
    if (typeof legacy === 'number' && legacy !== 0) return -legacy;
  }
  // Infer from role archetype hint when available — covers neutral
  // drill scenes where actor.team is unset on defenders.
  const role = actor && actor.role ? resolveRole(actor.role) : null;
  const hint = role && role.archetype_hint;
  if (hint === 'lineman' && actor && actor.team === 'defense') return Math.PI;
  if (actor && actor.team === 'defense') return Math.PI;
  return 0;
}

export default class FootballScene3D {
  constructor(renderer) {
    this.renderer = renderer;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x86b7e0);
    this.scene.fog = new THREE.Fog(0xb8d4ec, 80, 320);
    this.manifest = null;
    this.fieldBuilder = null;
    this.cameraCtrl = null;
    this.annotationRenderer = null;
    this.playerGroups = {};
    this.actors = [];
    this.qbActorId = null;
    this._animTargets = {};
    this._animStart = {};
    this._animStartTime = 0;
    this._tweenMsBase = 400;
    // Playback speed multiplier (1.0 = native). The session UI can set
    // this from 0.5× ... 2× via the `set-playback-speed` event so coaches
    // can step through plays slowly during chalk-talk and snap forward
    // during pure preview. Higher speed → shorter effective tween.
    this._playbackSpeed = 1.0;
    this._tweenMs = 400;
    this._cameraOptionsById = {};
    this._pendingAnnotations = null;
    this._clockTimer = null;
    this._clockStartTime = 0;
    this._clockDuration = 5000;
    this._clockWarning = 2000;
    this._clockRunning = false;
    this._disposed = false;
    this._lastFrameTime = 0;
    this._actorSpeeds = {};
    this._setActorsSeq = 0;

    // Top-down chalk-talk overlay: a single Group that we own, that
    // sits at the scene root (NOT inside any player group). Children
    // are flat colored discs + jersey-number sprites for each actor.
    // We update positions every frame from this.playerGroups, and we
    // toggle the whole overlay's .visible based on the active camera.
    // Putting markers here (instead of as children of player groups)
    // sidesteps a long-debugged scene-graph issue where children
    // added to the SkinnedMesh-rooted player group simply did not
    // render — only the GLB mesh itself made it through.
    this.topDownOverlay = new THREE.Group();
    this.topDownOverlay.name = 'topDownOverlay';
    this.topDownOverlay.visible = false; // off until camera is top_down
    this.topDownMarkers = {}; // actorId → Group
    this.scene.add(this.topDownOverlay);
  }

  init() {
    const hemi = new THREE.HemisphereLight(0xbfd8ee, 0x3d5b30, 0.6);
    hemi.position.set(0, 80, 0);
    this.scene.add(hemi);

    const sun = new THREE.DirectionalLight(0xfff1d9, 1.05);
    sun.position.set(60, 90, 40);
    sun.target.position.set(50, 0, 26);
    this.scene.add(sun);
    this.scene.add(sun.target);

    const fill = new THREE.DirectionalLight(0xa8c2dd, 0.25);
    fill.position.set(40, 50, -40);
    this.scene.add(fill);

    preloadHumanoid().catch((err) => {
      console.warn('FootballScene3D: humanoid preload failed', err);
    });

    this._onManifest = (m) => this.applyManifest(m);
    this._onActors = (a) => this.setActors(a);
    this._onFrame = (f) => this.updateFrame(f);
    this._onCamera = (id) => this.switchCamera(id);
    this._onAnnotations = (ann) => this.applyAnnotations(ann);
    this._onStartClock = (cfg) => this._startClock(cfg);
    this._onStopClock = () => this._stopClock();
    this._onPlaybackSpeed = (speed) => this.setPlaybackSpeed(speed);
    this._onFollowTarget = (payload) => this.setFollowTarget(payload);
    // 2026-06 — Coach playbook view. Orientation flips the orthographic
    // top-down camera so the coach app sees the field vertically (end
    // zone at top, offense moving up) while the learner stays in the
    // legacy landscape broadcast view. Stored on the scene so a
    // re-construction of the cameraCtrl during applyManifest replays
    // the latest orientation without dropping back to landscape.
    this._orientation = 'landscape';
    this._onOrientation = (o) => this.setOrientation(o);

    EventBus.on('set-manifest', this._onManifest);
    EventBus.on('set-actors', this._onActors);
    EventBus.on('update-frame', this._onFrame);
    EventBus.on('switch-camera', this._onCamera);
    EventBus.on('set-annotations', this._onAnnotations);
    EventBus.on('start-clock', this._onStartClock);
    EventBus.on('stop-clock', this._onStopClock);
    EventBus.on('set-playback-speed', this._onPlaybackSpeed);
    EventBus.on('set-follow-target', this._onFollowTarget);
    EventBus.on('set-orientation', this._onOrientation);

    EventBus.emit('scene-ready');
  }

  applyManifest(manifest) {
    this.manifest = manifest;
    this._tweenMsBase = manifest?.animation_config?.tween_duration_ms || 400;
    this._tweenMs = this._tweenMsBase / Math.max(this._playbackSpeed, 0.1);

    // Sprint 2 — uniform_overrides may ship inline on the manifest
    // when the runtime composes manifest + scene before passing it
    // here. SessionPage / SceneDesignerPage do this for the live
    // preview; legacy scenes leave the field empty and we fall back
    // to manifest entity_visuals.
    this._uniformOverrides = manifest?.uniform_overrides || {};

    this._cameraOptionsById = {};
    (manifest?.camera_options || []).forEach((opt) => {
      if (opt?.camera_id) this._cameraOptionsById[opt.camera_id] = opt;
    });

    if (this.fieldBuilder) this.fieldBuilder.dispose();
    const vp = manifest?.viewport || { width: 100, height: 53.3 };
    this.fieldBuilder = new FieldBuilder(this.scene, vp);
    this.fieldBuilder.build(manifest?.background_layers || []);

    const canvas = this.renderer.domElement;
    const aspect = canvas.width / canvas.height;
    if (this.cameraCtrl) this.cameraCtrl.dispose();
    this.cameraCtrl = new CameraController(vp, aspect, this._orientation);

    if (this.annotationRenderer) this.annotationRenderer.dispose();
    this.annotationRenderer = new AnnotationRenderer3D(this.scene);

    const defaultCam = (manifest?.camera_options || []).find((c) => c.is_default)?.camera_id;
    if (defaultCam) this.switchCamera(defaultCam);
  }

  /**
   * Replay the active orientation onto the (possibly newly-constructed)
   * camera controller. Called both from the EventBus handler and on
   * applyManifest() so a manifest swap doesn't briefly drop coach-app
   * back to landscape between camera-controller teardown and the next
   * `set-orientation` event.
   */
  setOrientation(orientation) {
    const next = (orientation === 'portrait' || orientation === 'landscape') ? orientation : 'landscape';
    this._orientation = next;
    if (this.cameraCtrl && typeof this.cameraCtrl.setOrientation === 'function') {
      this.cameraCtrl.setOrientation(next);
    }
  }

  setActors(actors) {
    this.actors = actors;
    this._setActorsSeq += 1;
    const seq = this._setActorsSeq;

    Object.values(this.playerGroups).forEach((g) => {
      disposePlayer(g);
      this.scene.remove(g);
    });
    this.playerGroups = {};
    this._actorSpeeds = {};
    this.qbActorId = null;

    Object.values(this.topDownMarkers).forEach((m) => {
      this.topDownOverlay.remove(m);
    });
    this.topDownMarkers = {};

    // De-stack actors that share the same position. Nudge colliders
    // apart by 1.2yd along the lateral axis so they don't overlap.
    const posMap = {};
    actors.forEach((actor) => {
      const pos = actor.initial_state?.position || { x: 0, y: 0 };
      const key = `${Math.round(pos.x * 10)}_${Math.round(pos.y * 10)}`;
      (posMap[key] = posMap[key] || []).push(actor);
    });
    Object.values(posMap).forEach((group) => {
      if (group.length < 2) return;
      const spread = 1.2;
      const startOffset = -((group.length - 1) * spread) / 2;
      group.forEach((actor, i) => {
        if (!actor.initial_state) actor.initial_state = {};
        if (!actor.initial_state.position) actor.initial_state.position = { x: 50, y: 26.65 };
        actor.initial_state.position.y += startOffset + i * spread;
      });
    });

    const entityVisuals = this.manifest?.entity_visuals || {};

    // Sprint 5 — Pre-warm the manifest clip cache for verbs likely to
    // appear in this scene. We start with a sane baseline (every scene
    // needs idle + the football_stance for pre-snap) plus any explicit
    // assignments authored on the actors. The locomotion clips are
    // also fetched eagerly — they're tiny (~70KB each) and almost
    // every scene uses them.
    const verbsInScene = new Set(['idle', 'football_stance', 'jog', 'sprint', 'backpedal']);
    actors.forEach((a) => {
      if (a?.initial_state?.assignment) verbsInScene.add(a.initial_state.assignment);
    });
    preloadClipsForVerbs(Array.from(verbsInScene)).catch(() => { /* best-effort */ });

    actors.forEach((actor) => {
      const visual = entityVisuals[actor.team] || entityVisuals.default || {};
      const uniformSpec = (this._uniformOverrides || {})[actor.team] || null;
      const pos = actor.initial_state?.position || { x: 0, y: 0 };

      Promise.resolve(createPlayer(actor, visual, uniformSpec)).then((group) => {
        if (this._disposed || seq !== this._setActorsSeq) {
          disposePlayer(group);
          return;
        }
        group.position.set(pos.x, 0, pos.y);

        // Sprint 6 — Default facing per team, with mesh forward-axis
        // correction.
        //
        // Convention: rotation.y = 0 is "facing +X" (downfield).
        // Different mesh exports of the same Mixamo skeleton land with
        // different bind-pose forward axes (Vanguard → +X, X-Bot → +Z),
        // so we add `meshFacingOffset(meshId)` to every desired facing.
        // Without this offset, X-Bot players were facing the home/away
        // sideline instead of the opposing end zone — coaches reported
        // "players facing different sidelines randomly."
        //
        // Resolution order for the desired pre-snap facing:
        //   1. actor.initial_state.facing_yaw (DSL field, radians,
        //      authored per actor in the scene). This is the new
        //      canonical field — when present we use it verbatim.
        //   2. actor.initial_state.facing (legacy NEGATED-radian
        //      field — old scenes still set this; we keep the negate
        //      to stay backward compatible).
        //   3. team-based default (offense→+X, defense→−X), with the
        //      QB inferred toward downfield.
        //
        // This fix follows the coach report "I am seeing some scenes
        // where the players are not oriented correctly at all" —
        // root cause was step (1) being silently ignored.
        const requestedMeshId = resolveRole(actor.role).mesh;
        const meshId = group.userData.resolvedArchetype || requestedMeshId;
        const meshOffset = meshFacingOffset(meshId);
        const baseY = _resolveBaseFacing(actor);
        group.rotation.y = baseY + meshOffset;
        // Cache so updateFrame() can re-apply the offset when motion
        // tweens override rotation based on velocity direction. We
        // also cache the authored base facing so the stationary path
        // in updateFrame can preserve it across keyframes that don't
        // explicitly re-author facing_yaw.
        group.userData.facingOffset = meshOffset;
        group.userData.baseFacing = baseY;

        // Sprint 4 — Football "ready stance". Mixamo's Idle leaves
        // players standing straight up which doesn't read as a
        // football pre-snap pose. We tip the hips forward ~12° so
        // every player looks like they're in a coiled athletic
        // stance, matching the silhouette GoArmy Edge ships. When
        // the play starts and locomotion takes over the mixer
        // crossfade smoothly resets the pose.
        applyReadyStance(group, actor);

        this.scene.add(group);
        this.playerGroups[actor.actor_id] = group;

        // Demo-sweep instrumentation: expose player count + actor list
        // on a global so playwright_demo_sweep.py can verify all 22
        // actors made it onto the field after each scene loads. Read-
        // only; writing to it from outside is harmless (we overwrite
        // each time setActors completes another player). See
        // scripts/playwright_demo_sweep.py.
        if (typeof window !== 'undefined') {
          const dbg = window.__SCENE_DEBUG__ = window.__SCENE_DEBUG__ || {};
          dbg.playerCount = Object.keys(this.playerGroups).length;
          dbg.actorIds = Object.keys(this.playerGroups);
        }

        // Create the top-down chalk-talk marker for this actor in the
        // sibling overlay group. We position it at the player's
        // current world position; _refreshTopDownMarkers() will keep
        // it tracking on every frame.
        try {
          const visualForMarker = (this.manifest?.entity_visuals || {})[actor.team]
            || (this.manifest?.entity_visuals || {}).offense || { color: '#888888' };
          const markerColor = resolvePlayerMarkerColor(actor, visualForMarker, uniformSpec);
          const marker = makeTopDownPlayerMarker(actor, markerColor);
          marker.position.set(pos.x, 0, pos.y);
          marker.rotation.y = group.rotation.y;
          this.topDownOverlay.add(marker);
          this.topDownMarkers[actor.actor_id] = marker;
        } catch (markerErr) {
          // Markers are nice-to-have; never block the player render.
          console.warn('FootballScene3D: top-down marker failed for', actor.actor_id, markerErr);
        }

        // Apply current camera's label visibility to the just-added
        // group; without this, labels created mid-camera-switch
        // would render at full size in QB view.
        if (this.cameraCtrl) {
          setLabelsVisibleForCamera({ [actor.actor_id]: group }, this.cameraCtrl.activeCameraId);
        }

        if (actor.role === 'quarterback') {
          this.qbActorId = actor.actor_id;
          if (this.cameraCtrl) this.cameraCtrl.setQBPosition(pos.x, pos.y);
        }

        if (this.annotationRenderer) {
          this.annotationRenderer.setPlayerGroups(this.playerGroups);
        }

        if (this._pendingAnnotations) {
          this._redrawAnnotations();
        }
      }).catch((err) => {
        console.error('FootballScene3D: createPlayer failed for', actor.actor_id, err);
      });
    });
  }

  updateFrame(frame) {
    if (!frame || !frame.actor_positions) return;

    this._animStartTime = performance.now();
    this._animStart = {};
    this._animTargets = {};

    Object.entries(frame.actor_positions).forEach(([actorId, state]) => {
      const group = this.playerGroups[actorId];
      if (!group || !state?.position) return;

      const startPos = group.position.clone();
      const targetPos = new THREE.Vector3(state.position.x, 0, state.position.y);
      this._animStart[actorId] = startPos;
      this._animTargets[actorId] = targetPos;

      const dist = startPos.distanceTo(targetPos);
      const tweenSec = Math.max(this._tweenMs, 1) / 1000;
      this._actorSpeeds[actorId] = dist / tweenSec;

      // Sprint 5 — Layer-1 (coach authoring) → Layer-3 (renderer):
      //
      //   ActorState.assignment  → "pass_block" | "slant" | "drop_5" | …
      //   ActorState.intensity   → "walk" | "jog" | "sprint" | "break"
      //
      // The renderer hands these to PlayerModel.applyAssignment which
      // looks them up in AssetManifest.VERBS, lazy-loads the right
      // clip, and crossfades. Coaches author them; the AI suggest
      // agent emits them; the renderer just executes. Legacy scenes
      // (no assignment) fall through to speed-based locomotion.
      const assignment = state?.assignment || null;
      const intensity = state?.intensity || null;
      const moveType = state?.metadata?.movement_type || null;
      this._actorMovement = this._actorMovement || {};
      this._actorMovement[actorId] = moveType;

      const velocity = new THREE.Vector3(
        targetPos.x - startPos.x,
        0,
        targetPos.z - startPos.z,
      );
      if (tweenSec > 0) velocity.divideScalar(tweenSec);

      // Manifest-driven path — handles assignment verb OR locomotion
      // fallback. Returns a Promise but we don't await; the existing
      // mesh keeps rendering with whatever clip was previously active
      // until the new one mounts (250ms crossfade absorbs the gap).
      applyAssignment(group, assignment, intensity, {
        speed: this._actorSpeeds[actorId],
        velocity,
        fade_ms: 250,
      }).catch(() => {});

      // Legacy speed/movement_type pipeline kept as a defensive backstop
      // for any code path that still depends on the old animation
      // registration table (e.g. tier-D GLBs that ship pre-baked clips).
      setPlayerLocomotion(group, this._actorSpeeds[actorId], moveType);

      const offset = group.userData?.facingOffset || 0;
      if (dist > 0.05) {
        const dx = targetPos.x - startPos.x;
        const dz = targetPos.z - startPos.z;
        // Add the mesh's forward-axis correction so the motion-derived
        // facing matches the per-mesh convention. Without it, X-Bot
        // players turning to run a route would end up facing 90° off.
        group.rotation.y = Math.atan2(dx, dz) - Math.PI / 2 + offset;
      } else {
        // Stationary keyframe — preserve authored facing_yaw if the
        // event re-asserted it; otherwise keep the cached base facing
        // captured at setActors() time. This fixes the report
        // "I am seeing some scenes where the players are not oriented
        // correctly at all" — the previous code left rotation.y at
        // whatever the prior tween left behind, so a defender that
        // tracked a route on event N would still be facing that
        // heading on event N+1 even after the scene re-authored
        // facing_yaw=π.
        let stationaryY = null;
        if (typeof state.facing_yaw === 'number') {
          stationaryY = state.facing_yaw;
        } else if (typeof state.facing === 'number' && state.facing !== 0) {
          stationaryY = -state.facing;
        } else if (typeof group.userData.baseFacing === 'number') {
          stationaryY = group.userData.baseFacing;
        }
        if (stationaryY !== null) {
          group.rotation.y = stationaryY + offset;
        }
      }

      if (actorId === this.qbActorId && this.cameraCtrl) {
        this.cameraCtrl.setQBPosition(state.position.x, state.position.y);
      }
    });

    if (this._pendingAnnotations) {
      setTimeout(() => this._redrawAnnotations(), this._tweenMs + 50);
    }
  }

  switchCamera(cameraId) {
    if (this.cameraCtrl) {
      const opt = this._cameraOptionsById[cameraId];
      this.cameraCtrl.switchCamera(cameraId, { followConfig: opt?.config });
    }
    // Player labels are 1.6 yd-tall world sprites; readable from
    // 80m up but they swallow the screen at QB eye height. Hide
    // them in any non-top-down camera; setActors and the initial
    // applyManifest call also funnel through here so the state
    // stays in sync as actors load.
    setLabelsVisibleForCamera(this.playerGroups, cameraId);
    // Show the chalk-talk disc overlay only in top_down — it would
    // poke through a player's feet in any perspective view.
    if (this.topDownOverlay) {
      this.topDownOverlay.visible = (cameraId === 'top_down');
    }
    EventBus.emit('camera-changed', cameraId);
  }

  // Sync each marker's world position + facing to its player group.
  // Cheap (O(actors)) — called from update() each frame so markers
  // track in-flight tweens during play animation.
  _refreshTopDownMarkers() {
    if (!this.topDownOverlay || !this.topDownOverlay.visible) return;
    Object.entries(this.topDownMarkers).forEach(([actorId, marker]) => {
      const pg = this.playerGroups[actorId];
      if (!pg) return;
      marker.position.set(pg.position.x, 0, pg.position.z);
      marker.rotation.y = pg.rotation.y;
    });
  }

  setPlaybackSpeed(speed) {
    const next = Math.max(0.25, Math.min(4.0, Number(speed) || 1.0));
    this._playbackSpeed = next;
    this._tweenMs = this._tweenMsBase / next;
  }

  /**
   * payload may be either a string actor_id (legacy) or
   * { actor_id, position } from the session UI's follow-player picker.
   */
  setFollowTarget(payload) {
    if (!this.cameraCtrl) return;
    if (typeof payload === 'string') {
      const group = this.playerGroups[payload];
      this.cameraCtrl.setFollowTarget(payload, group ? group.position : null);
    } else if (payload && typeof payload === 'object') {
      const id = payload.actor_id || null;
      const group = id ? this.playerGroups[id] : null;
      this.cameraCtrl.setFollowTarget(id, group ? group.position : payload.position);
    } else {
      this.cameraCtrl.setFollowTarget(null, null);
    }
  }

  applyAnnotations(annotations) {
    this._pendingAnnotations = annotations;
    this._redrawAnnotations();
  }

  /**
   * Sprint 2 — Coach chalk-talk strokes for the current event.
   * Called by the runtime alongside applyAnnotations(). Strokes from
   * Scene.telestration (scene-wide) and TimelineEvent.telestration
   * (per-event) should be merged by the caller before invoking this;
   * we just render whatever we're given.
   */
  applyTelestration(strokes) {
    this._pendingTelestration = strokes || [];
    this._redrawAnnotations();
  }

  _redrawAnnotations() {
    if (!this.annotationRenderer) return;
    if (Object.keys(this.playerGroups).length === 0) return;
    const anns = this._pendingAnnotations || [];
    const strokes = this._pendingTelestration || [];
    if (anns.length === 0 && strokes.length === 0) return;
    this.annotationRenderer.setPlayerGroups(this.playerGroups);
    this.annotationRenderer.apply(anns);
    if (strokes.length > 0) {
      this.annotationRenderer.applyTelestration(strokes);
    }
  }

  _startClock(cfg) {
    this._stopClock();
    if (cfg) {
      this._clockDuration = cfg.duration_ms || 5000;
      this._clockWarning = cfg.warning_ms || 2000;
    }
    this._clockStartTime = performance.now();
    this._clockRunning = true;
  }

  _stopClock() {
    this._clockRunning = false;
  }

  update(now) {
    if (this._disposed) return;

    const dtSec = this._lastFrameTime ? Math.min(0.1, (now - this._lastFrameTime) / 1000) : 0;
    this._lastFrameTime = now;

    // Tag players with LOD tier (NEAR/MID/FAR/CULL) before stepping
    // mixers so distant skeletons skip frames or stop entirely.
    const cam = this.cameraCtrl ? this.cameraCtrl.activeCamera : null;
    applyLOD(this.playerGroups, cam);

    updatePlayerMixers(this.playerGroups, dtSec);

    const elapsed = now - this._animStartTime;
    const t = Math.min(1, elapsed / this._tweenMs);
    const easedT = t < 1 ? 1 - Math.pow(1 - t, 2) : 1;

    Object.entries(this._animTargets).forEach(([actorId, target]) => {
      const group = this.playerGroups[actorId];
      const start = this._animStart[actorId];
      if (!group || !start) return;
      group.position.lerpVectors(start, target, easedT);
      if (t >= 1 && this._actorSpeeds[actorId] > 0) {
        this._actorSpeeds[actorId] = 0;
        setPlayerLocomotion(group, 0);
      }
    });

    // Chase cameras need per-frame updates so they smoothly follow
    // mid-tween motion rather than snapping at event boundaries.
    if (this.cameraCtrl && typeof this.cameraCtrl.tick === 'function') {
      this.cameraCtrl.tick(this.playerGroups);
    }

    if (this.annotationRenderer) {
      this.annotationRenderer.update(now);
    }

    // Sync chalk-talk markers to player positions (only does work when
    // top-down overlay is currently visible).
    this._refreshTopDownMarkers();

    if (this._clockRunning) {
      const clockElapsed = now - this._clockStartTime;
      if (clockElapsed >= this._clockDuration) {
        this._clockRunning = false;
        EventBus.emit('clock-expired');
      }
    }
  }

  render() {
    if (this._disposed || !this.cameraCtrl) return;
    this.renderer.render(this.scene, this.cameraCtrl.activeCamera);
  }

  resize(width, height) {
    if (this.cameraCtrl) {
      this.cameraCtrl.resize(width / height);
    }
  }

  dispose() {
    this._disposed = true;

    EventBus.off('set-manifest', this._onManifest);
    EventBus.off('set-actors', this._onActors);
    EventBus.off('update-frame', this._onFrame);
    EventBus.off('switch-camera', this._onCamera);
    EventBus.off('set-annotations', this._onAnnotations);
    EventBus.off('start-clock', this._onStartClock);
    EventBus.off('stop-clock', this._onStopClock);
    EventBus.off('set-playback-speed', this._onPlaybackSpeed);
    EventBus.off('set-follow-target', this._onFollowTarget);
    EventBus.off('set-orientation', this._onOrientation);

    if (this.annotationRenderer) this.annotationRenderer.dispose();
    if (this.fieldBuilder) this.fieldBuilder.dispose();
    if (this.cameraCtrl) this.cameraCtrl.dispose();

    Object.values(this.playerGroups).forEach((g) => {
      disposePlayer(g);
      this.scene.remove(g);
    });

    this.scene.traverse((obj) => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (obj.material.map) obj.material.map.dispose();
        obj.material.dispose();
      }
    });
  }
}

RendererRegistry.register('football_3d', FootballScene3D, 'three_3d');
